import { useEffect, useState } from 'react';

import { getErrorMessage } from '../../../utils/errors';
import type { MappedData } from '../types';

export function makeGeoDataApi<T, A extends unknown[]>(
  performApiRequest: (...args: A) => Promise<T[]>,
  getRequestKey: (...args: A) => string,
  getItemKey: (item: T) => string,
) {
  const ongoingRequests: Partial<Record<string, Promise<MappedData<T>>>> = {};

  async function fetchGeoData(...args: A): Promise<MappedData<T>> {
    const requestKey = getRequestKey(...args);
    const ongoingRequest = ongoingRequests[requestKey];

    if (ongoingRequest) {
      return ongoingRequest;
    }

    const freshRequest: Promise<MappedData<T>> = performApiRequest(...args)
      .then((res) => ({
        list: res,
        map: res.reduce<Record<string, T>>((map, item) => {
          map[getItemKey(item)] = item;

          return map;
        }, {}),
      }))
      .catch((error) => {
        const errorMessage = getErrorMessage(error);
        console.error(`Failed to fetch GeoData: ${errorMessage}`, args, performApiRequest.name);

        return { list: [], map: {} };
      });

    ongoingRequests[requestKey] = freshRequest;

    return freshRequest;
  }

  function useFullGeoData(...args: A): [MappedData<T>, boolean] {
    const [geoData, setGeoData] = useState<MappedData<T>>({ list: [], map: {} });
    const [isFetching, setIsFetching] = useState(true);

    useEffect(() => {
      void fetchGeoData(...args).then((freshGeoData) => {
        setGeoData(freshGeoData);
        setIsFetching(false);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, args);

    return [geoData, isFetching];
  }

  return { fetchGeoData, useFullGeoData };
}
